import {
  AlertTriangle,
  ArrowUpRight,
  Flag,
  RefreshCcw,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type {
  CycleResponse,
  PositionResponse,
  StopMovementResponse,
} from "../../api/client";
import type { WsEvent } from "../../api/ws";
import { breakerPayload, collapseBreakerRuns } from "@/lib/breakerEvents";
import { cn } from "@/lib/utils";

interface CycleTimelineProps {
  cycle: CycleResponse | null;
  positions: PositionResponse[] | null;
  stopMovements: StopMovementResponse[] | null;
  wsEvents?: WsEvent[];
}

interface TimelineEvent {
  ts: number;
  iso: string;
  type: string;
  detail: string;
  kind: "cycle" | "position" | "position-closed" | "stop" | "breaker" | "reconcile";
  /** Breaker code (kind="breaker" only) — the PV4 run-collapse key. */
  code?: string;
  /** Raw breaker detail (kind="breaker" only) — varies per tick (spread 5.00↔7.00). */
  rawDetail?: string;
}

const KIND_META: Record<
  TimelineEvent["kind"],
  { Icon: LucideIcon; color: string }
> = {
  cycle: { Icon: Flag, color: "text-info" },
  position: { Icon: ArrowUpRight, color: "text-buy" },
  "position-closed": { Icon: XCircle, color: "text-muted-foreground" },
  stop: { Icon: ShieldCheck, color: "text-buy" },
  breaker: { Icon: AlertTriangle, color: "text-warning" },
  reconcile: { Icon: RefreshCcw, color: "text-warning" },
};

/**
 * Compact chronological timeline for the active cycle, synthesised from
 * read-only sources: cycle start/end, position open/close, stop movements
 * (rejected widening preserved, SPEC §19.5), and in-session breaker events.
 */
export default function CycleTimeline({
  cycle,
  positions,
  stopMovements,
  wsEvents,
}: CycleTimelineProps) {
  if (cycle == null) {
    return <p className="text-muted-foreground text-sm">No active cycle.</p>;
  }
  const events: TimelineEvent[] = [];

  pushIso(events, cycle.startedAt, {
    type: "cycle.started",
    detail: `bias=${cycle.bias}`,
    kind: "cycle",
  });

  if (cycle.endedAt) {
    pushIso(events, cycle.endedAt, {
      type: "cycle.ended",
      detail: cycle.endReason ?? "—",
      kind: "cycle",
    });
  }

  for (const p of positions ?? []) {
    pushIso(events, p.openedAt, {
      type: "position.opened",
      detail: `${p.direction} ${p.role} deal=${p.dealId.slice(0, 8)}… entry=${Number(p.entryPrice).toFixed(2)}`,
      kind: "position",
    });
    if (p.closedAt) {
      const synthetic = p.exitReason === "RECONCILED_EXTERNALLY_CLOSED";
      pushIso(events, p.closedAt, {
        type: synthetic ? "position.reconciled" : "position.closed",
        detail: `${p.direction} ${p.role} deal=${p.dealId.slice(0, 8)}… exit=${
          p.closePrice == null ? "—" : Number(p.closePrice).toFixed(2)
        } reason=${p.exitReason ?? "—"}`,
        kind: synthetic ? "reconcile" : "position-closed",
      });
    }
  }

  for (const s of stopMovements ?? []) {
    pushIso(events, s.proposedAt, {
      type: s.accepted ? "stop.advanced" : "stop.rejected",
      detail: `pos=${s.positionId} ${s.oldStop == null ? "—" : Number(s.oldStop).toFixed(2)} → ${Number(s.newStop).toFixed(2)} ${
        s.kind
      }${s.accepted ? "" : ` (${s.skipReason ?? "rejected"})`}`,
      kind: "stop",
    });
  }

  const cycleStartedTs = Date.parse(cycle.startedAt);
  for (const ev of wsEvents ?? []) {
    if (ev.type !== "circuit_breaker.triggered") continue;
    if (!ev.ts) continue;
    const ts = Date.parse(ev.ts);
    if (Number.isNaN(ts)) continue;
    if (cycle.endedAt) {
      const end = Date.parse(cycle.endedAt);
      if (!Number.isNaN(end) && ts > end) continue;
    }
    if (!Number.isNaN(cycleStartedTs) && ts < cycleStartedTs) continue;
    const payload = breakerPayload(ev);
    if (payload == null) continue;
    events.push({
      ts,
      iso: ev.ts,
      type: "circuit_breaker.triggered",
      detail: [payload.code, payload.detail].filter(Boolean).join(" — "),
      kind: "breaker",
      code: payload.code,
      rawDetail: payload.detail,
    });
  }

  events.sort((a, b) => a.ts - b.ts);
  // PV4 — collapse des rafales de breakers consécutives de même code (lib partagée).
  const rows = collapseBreakerRuns(events, (first, last, count) => ({
    ts: first.ts,
    iso: first.iso,
    type: "circuit_breaker.triggered",
    detail: `${first.code} ×${count} (${formatTimeOfDay(first.iso)} → ${formatTimeOfDay(last.iso)})${
      last.rawDetail ? ` — ${last.rawDetail}` : ""
    }`,
    kind: "breaker" as const,
    code: first.code,
  }));

  if (rows.length === 0) {
    return <p className="text-muted-foreground text-sm">No timeline events yet.</p>;
  }

  return (
    <ol className="timeline flex flex-col" data-testid="cycle-timeline">
      {rows.map((e, i) => {
        const meta = KIND_META[e.kind];
        return (
          <li
            key={`${e.ts}-${i}-${e.type}`}
            className="timeline__row border-border/50 flex items-start gap-2.5 border-b py-1.5 text-sm last:border-b-0"
            data-testid={`timeline-${e.type}`}
          >
            <meta.Icon
              className={cn("mt-0.5 size-3.5 shrink-0", meta.color)}
              aria-hidden
            />
            <time className="timeline__ts text-muted-foreground w-16 shrink-0 font-mono text-xs tabular-nums">
              {formatTimeOfDay(e.iso)}
            </time>
            <span
              className={cn(
                "timeline__type shrink-0 font-mono text-xs font-semibold",
                meta.color,
              )}
            >
              {e.type}
            </span>
            <span className="text-muted-foreground min-w-0 flex-1 break-words font-mono text-xs">
              {e.detail}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

function pushIso(
  out: TimelineEvent[],
  iso: string,
  rest: Omit<TimelineEvent, "ts" | "iso">,
): void {
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return;
  out.push({ ts, iso, ...rest });
}

function formatTimeOfDay(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return new Date(t).toLocaleTimeString("en-GB", { hour12: false });
}
